// SPDX-License-Identifier: AGPL-3.0-or-later

import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { useMe } from '../../api/auth.js';
import {
  useResponse,
  useResponseAudit,
  useResponseNotes,
  useCreateNote,
  useUpdateNote,
  useDeleteNote,
  useUpdateResponseStatus,
  type AuditTimelineEntry,
  type ResponseNote,
} from '../../api/responses.js';
import type { ResponseEligibility } from '../../api/types.js';
import { Button, Card, useToast } from '@intakeform/ui';
import type { QuestionnaireItem, Questionnaire } from '@intakeform/fhir';
import type { Finding } from '@intakeform/ui';
import { flagReasons } from './flag-reasons.js';
import {
  ArrowLeft,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Mail,
  Phone,
  Calendar,
  FileText,
  HelpCircle,
  History,
  Pencil,
  Shield,
  ShieldOff,
  StickyNote,
  Trash2,
  X,
  type LucideIcon,
} from 'lucide-react';

/* ------------------------------------------------------------------ */
/*  Human-readable label helpers                                      */
/* ------------------------------------------------------------------ */

/**
 * Convert a slug/linkId like "dob", "first-name", or "has_allergies"
 * into a readable label: "DOB", "First Name", "Has Allergies".
 *
 * Common medical abbreviations are handled with a lookup table.
 */
const KNOWN_ABBREVIATIONS: Record<string, string> = {
  dob: 'Date of Birth',
  ssn: 'Social Security Number',
  id: 'ID',
  mrn: 'Medical Record Number',
  npi: 'NPI',
  rx: 'Prescription',
  dx: 'Diagnosis',
  hx: 'History',
  bp: 'Blood Pressure',
  hr: 'Heart Rate',
  bmi: 'BMI',
  ekg: 'EKG',
  ecg: 'ECG',
  emr: 'EMR',
  ehr: 'EHR',
  pcp: 'Primary Care Provider',
  ins: 'Insurance',
  appt: 'Appointment',
  lang: 'Language',
  tel: 'Telephone',
  addr: 'Address',
  fname: 'First Name',
  lname: 'Last Name',
  mname: 'Middle Name',
};

function humanizeSlug(slug: string): string {
  // Check if the entire slug is a known abbreviation
  const lower = slug.toLowerCase();
  if (KNOWN_ABBREVIATIONS[lower]) return KNOWN_ABBREVIATIONS[lower];

  // Split on hyphens, underscores, or camelCase boundaries
  const words = slug
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((w) => {
      const wl = w.toLowerCase();
      // Check individual word abbreviations
      if (KNOWN_ABBREVIATIONS[wl]) return KNOWN_ABBREVIATIONS[wl];
      // Title-case the word
      return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
    });

  return words.join(' ');
}

/* ------------------------------------------------------------------ */
/*  Answer formatting                                                 */
/* ------------------------------------------------------------------ */

type AnswerObj = Record<string, unknown>;
function isAnswerObj(x: unknown): x is AnswerObj {
  return typeof x === 'object' && x !== null;
}
function strOrUndef(x: unknown): string | undefined {
  return typeof x === 'string' ? x : undefined;
}

function formatAnswer(answer: unknown): string {
  if (answer === null || answer === undefined) return '';
  if (!isAnswerObj(answer)) return String(answer);

  const coding = answer['valueCoding'];
  if (isAnswerObj(coding)) {
    return strOrUndef(coding['display']) ?? strOrUndef(coding['code']) ?? '';
  }
  if (typeof answer['valueString'] === 'string') return answer['valueString'];
  if (typeof answer['valueInteger'] === 'number') return String(answer['valueInteger']);
  if (typeof answer['valueDecimal'] === 'number') return String(answer['valueDecimal']);
  if (typeof answer['valueDate'] === 'string') return formatDate(answer['valueDate']);
  if (typeof answer['valueDateTime'] === 'string') return formatDateTime(answer['valueDateTime']);
  if (typeof answer['valueTime'] === 'string') return answer['valueTime'];
  if (typeof answer['valueBoolean'] === 'boolean') return answer['valueBoolean'] ? 'Yes' : 'No';
  if (typeof answer['valueUri'] === 'string') return answer['valueUri'];
  const attachment = answer['valueAttachment'];
  if (isAnswerObj(attachment)) {
    return strOrUndef(attachment['title']) ?? strOrUndef(attachment['url']) ?? 'Attachment';
  }
  const quantity = answer['valueQuantity'];
  if (isAnswerObj(quantity)) {
    const value = quantity['value'];
    const unit = quantity['unit'];
    const valueStr = typeof value === 'number' || typeof value === 'string' ? String(value) : '';
    const unitStr = typeof unit === 'string' && unit !== '' ? ` ${unit}` : '';
    return `${valueStr}${unitStr}`;
  }
  const ref = answer['valueReference'];
  if (isAnswerObj(ref)) {
    return strOrUndef(ref['display']) ?? strOrUndef(ref['reference']) ?? '';
  }
  const firstVal = Object.values(answer)[0];
  if (typeof firstVal === 'object' && firstVal !== null) {
    return JSON.stringify(firstVal);
  }
  return String(firstVal ?? '');
}

function formatDate(dateStr: string): string {
  try {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
  } catch {
    return dateStr;
  }
}

function formatDateTime(dateStr: string): string {
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return dateStr;
  }
}

function isBooleanAnswer(answer: unknown): answer is { valueBoolean: boolean } {
  return (
    isAnswerObj(answer) && typeof (answer as { valueBoolean?: unknown }).valueBoolean === 'boolean'
  );
}

/* ------------------------------------------------------------------ */
/*  Build a linkId → QuestionnaireItem map for question text lookup    */
/* ------------------------------------------------------------------ */

function buildItemMap(questionnaire: Questionnaire | undefined): Map<string, QuestionnaireItem> {
  const map = new Map<string, QuestionnaireItem>();
  if (!questionnaire?.item) return map;
  const walk = (items: QuestionnaireItem[]) => {
    for (const item of items) {
      map.set(item.linkId, item);
      if (item.item) walk(item.item);
    }
  };
  walk(questionnaire.item);
  return map;
}

/**
 * Get a human-readable label for a question.
 * Priority: questionnaire item text > humanized slug
 */
function getQuestionLabel(linkId: string, itemMap: Map<string, QuestionnaireItem>): string {
  const item = itemMap.get(linkId);
  if (item?.text) return item.text;
  return humanizeSlug(linkId);
}

/* ------------------------------------------------------------------ */
/*  Build a tree structure from the questionnaire for section grouping */
/* ------------------------------------------------------------------ */

interface SectionGroup {
  linkId: string;
  text: string;
  children: string[]; // linkIds of direct children
}

function buildSections(questionnaire: Questionnaire | undefined): SectionGroup[] {
  if (!questionnaire?.item) return [];
  const sections: SectionGroup[] = [];
  for (const item of questionnaire.item) {
    if (item.type === 'group' && item.item) {
      sections.push({
        linkId: item.linkId,
        text: item.text ?? humanizeSlug(item.linkId),
        children: collectChildLinkIds(item.item),
      });
    }
  }
  return sections;
}

function collectChildLinkIds(items: QuestionnaireItem[]): string[] {
  const result: string[] = [];
  for (const item of items) {
    if (item.type !== 'group' && item.type !== 'display') {
      result.push(item.linkId);
    }
    if (item.item) {
      result.push(...collectChildLinkIds(item.item));
    }
  }
  return result;
}

/* ------------------------------------------------------------------ */
/*  Status helpers                                                    */
/* ------------------------------------------------------------------ */

const STATUS_OPTIONS = [
  { value: 'needs-review', label: 'Needs Review', color: 'var(--color-warning)' },
  { value: 'in-review', label: 'In Review', color: 'var(--color-info, 59 130 246)' },
  { value: 'verified', label: 'Verified', color: 'var(--color-success)' },
  { value: 'completed', label: 'Completed', color: 'var(--color-success)' },
  { value: 'in-progress', label: 'In Progress', color: 'var(--color-muted-foreground)' },
  { value: 'entered-in-error', label: 'Error', color: 'var(--color-danger)' },
];

function getStatusLabel(status: string): string {
  return STATUS_OPTIONS.find((s) => s.value === status)?.label ?? status;
}

function getStatusColor(status: string): string {
  return STATUS_OPTIONS.find((s) => s.value === status)?.color ?? 'var(--color-muted-foreground)';
}

/* ------------------------------------------------------------------ */
/*  Small reusable sub-components                                     */
/* ------------------------------------------------------------------ */

function MetaItem({
  icon: Icon,
  label,
  value,
}: {
  icon: LucideIcon;
  label: string;
  value: string;
}) {
  return (
    <div className="response-meta-item">
      <Icon size={14} className="response-meta-icon" />
      <div>
        <div className="response-meta-label">{label}</div>
        <div className="response-meta-value">{value}</div>
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const color = getStatusColor(status);
  return (
    <span
      className="response-status-badge"
      style={{
        background: `rgba(${color}, 0.12)`,
        color: `rgb(${color})`,
      }}
    >
      {status === 'verified' || status === 'completed' ? (
        <CheckCircle2 size={12} />
      ) : status === 'in-progress' || status === 'in-review' ? (
        <Clock size={12} />
      ) : status === 'entered-in-error' ? (
        <AlertTriangle size={12} />
      ) : (
        <Shield size={12} />
      )}
      {getStatusLabel(status)}
    </span>
  );
}

/**
 * Insurance eligibility status card (bead 27w). Sits between the
 * patient header and the form-level findings so clinicians notice
 * "this patient's coverage is inactive" before scrolling through
 * the answers. The shape is deliberately compact — the raw clearing-
 * house payload lives in the sidecar table; surfacing it in the UI
 * is a follow-up bead.
 */
function EligibilityCard({ eligibility }: { eligibility: ResponseEligibility }) {
  const { tone, icon: Icon, label } = eligibilityPresentation(eligibility.status);
  const checked = new Date(eligibility.checkedAt).toLocaleString();
  return (
    <Card tone={tone} variant="elevated" data-eligibility-card={eligibility.status}>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 'var(--space-3)',
          padding: '4px 0',
        }}
      >
        <Icon size={20} aria-hidden="true" />
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            <strong>{'Insurance: '}</strong>
            <span data-eligibility-status>{label}</span>
            {eligibility.planName !== null ? (
              <span className="muted" style={{ fontSize: 'var(--text-sm)' }}>
                {' · '}
                {eligibility.planName}
              </span>
            ) : null}
          </div>
          <div className="muted" style={{ fontSize: 'var(--text-xs)', marginTop: 4 }}>
            {`Checked ${checked} via ${eligibility.providerKind}`}
          </div>
          {eligibility.status === 'error' && eligibility.errorMessage !== null ? (
            <div style={{ fontSize: 'var(--text-xs)', marginTop: 4 }}>
              {eligibility.errorMessage}
            </div>
          ) : null}
        </div>
      </div>
    </Card>
  );
}

function eligibilityPresentation(status: ResponseEligibility['status']): {
  tone: 'success' | 'warning' | 'danger' | 'neutral';
  icon: LucideIcon;
  label: string;
} {
  switch (status) {
    case 'active':
      return { tone: 'success', icon: CheckCircle2, label: 'Active' };
    case 'inactive':
      return { tone: 'danger', icon: ShieldOff, label: 'Inactive' };
    case 'unknown':
      return { tone: 'warning', icon: HelpCircle, label: 'Unknown' };
    case 'error':
      return { tone: 'warning', icon: AlertTriangle, label: 'Check failed' };
  }
}

function BoolBadge({ value }: { value: boolean }) {
  return (
    <span className={`response-bool-badge ${value ? 'response-bool-yes' : 'response-bool-no'}`}>
      {value ? <CheckCircle2 size={12} /> : null}
      {value ? 'Yes' : 'No'}
    </span>
  );
}

function InlineFinding({ finding }: { finding: Finding }) {
  const isError = finding.severity === 'error';
  return (
    <div className={`response-inline-finding ${isError ? 'severity-error' : 'severity-warning'}`}>
      <AlertTriangle size={13} style={{ flexShrink: 0, marginTop: 1 }} />
      <span>{finding.message}</span>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Answer Row                                                        */
/* ------------------------------------------------------------------ */

function AnswerRow({
  linkId,
  questionText,
  answers,
  findings,
}: {
  linkId: string;
  questionText: string;
  answers: unknown[];
  findings: Finding[];
}) {
  return (
    <div className="response-answer-row">
      <div className="response-answer-pair">
        <div className="response-answer-label">{questionText}</div>
        <div className="response-answer-value">
          {answers.length === 0 ? (
            <span className="response-answer-empty">Not answered</span>
          ) : answers.length === 1 && isBooleanAnswer(answers[0]) ? (
            <BoolBadge value={answers[0].valueBoolean} />
          ) : (
            answers.map(formatAnswer).join(', ')
          )}
        </div>
      </div>
      {findings.map((f, i) => (
        <InlineFinding key={`${linkId}-f-${i}`} finding={f} />
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main route component                                              */
/* ------------------------------------------------------------------ */

/* ------------------------------------------------------------------ */
/*  Notes panel (intakeform-preview-bib)                              */
/* ------------------------------------------------------------------ */

function NotesPanel({
  responseId,
  currentUserId,
}: {
  responseId: string;
  currentUserId: string | undefined;
}) {
  const notes = useResponseNotes(responseId);
  const create = useCreateNote(responseId);
  const update = useUpdateNote(responseId);
  const remove = useDeleteNote(responseId);
  const { toast } = useToast();
  const [draft, setDraft] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editBuffer, setEditBuffer] = useState('');

  const submit = () => {
    const body = draft.trim();
    if (body === '') return;
    create.mutate(body, {
      onSuccess: () => {
        setDraft('');
        toast({ title: 'Note added', variant: 'success' });
      },
      onError: (err) =>
        toast({ title: 'Could not add note', description: err.message, variant: 'danger' }),
    });
  };

  const startEdit = (n: ResponseNote) => {
    setEditingId(n.id);
    setEditBuffer(n.body);
  };

  const saveEdit = (noteId: string) => {
    const body = editBuffer.trim();
    if (body === '') return;
    update.mutate(
      { noteId, body },
      {
        onSuccess: () => {
          setEditingId(null);
          toast({ title: 'Note updated', variant: 'success' });
        },
        onError: (err) =>
          toast({ title: 'Could not update note', description: err.message, variant: 'danger' }),
      },
    );
  };

  const removeNote = (n: ResponseNote) => {
    if (!window.confirm('Delete this note? Soft-deletes the row; an admin can recover it.')) return;
    remove.mutate(n.id, {
      onSuccess: () => toast({ title: 'Note deleted', variant: 'success' }),
      onError: (err) =>
        toast({ title: 'Could not delete note', description: err.message, variant: 'danger' }),
    });
  };

  return (
    <Card
      heading={
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <StickyNote size={16} /> Notes ({notes.data?.length ?? 0})
        </span>
      }
      variant="default"
      data-testid="notes-panel"
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
        <textarea
          data-testid="notes-composer"
          className="field-config-textarea"
          placeholder="Add internal context for this response — patient phone call, follow-up reminders, handoff notes…"
          value={draft}
          rows={3}
          onChange={(e) => setDraft(e.currentTarget.value)}
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="primary"
            disabled={create.isPending || draft.trim() === ''}
            onClick={submit}
          >
            {create.isPending ? 'Adding…' : 'Add note'}
          </Button>
        </div>
      </div>

      {notes.isLoading ? (
        <p className="muted">Loading notes…</p>
      ) : (notes.data ?? []).length === 0 ? (
        <p className="muted">No notes yet.</p>
      ) : (
        <ul
          style={{
            listStyle: 'none',
            padding: 0,
            margin: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
          }}
        >
          {(notes.data ?? []).map((n) => {
            const isAuthor = currentUserId === n.author.id;
            const isEditing = editingId === n.id;
            return (
              <li
                key={n.id}
                data-testid={`note-${n.id}`}
                style={{ borderTop: '1px solid var(--color-border, #e5e7eb)', paddingTop: 12 }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'baseline',
                    marginBottom: 4,
                  }}
                >
                  <div style={{ fontSize: 'var(--text-sm)', fontWeight: 600 }}>
                    {n.author.displayName}{' '}
                    <span className="muted" style={{ fontWeight: 400 }}>
                      · {n.author.role}
                    </span>
                  </div>
                  <div className="muted" style={{ fontSize: 'var(--text-xs)' }}>
                    {formatDateTime(n.createdAt)}
                    {n.updatedAt !== n.createdAt ? ' · edited' : ''}
                  </div>
                </div>
                {isEditing ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <textarea
                      className="field-config-textarea"
                      value={editBuffer}
                      rows={3}
                      onChange={(e) => setEditBuffer(e.currentTarget.value)}
                    />
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                      <Button variant="ghost" onClick={() => setEditingId(null)}>
                        <X size={14} /> Cancel
                      </Button>
                      <Button
                        variant="primary"
                        onClick={() => saveEdit(n.id)}
                        disabled={update.isPending}
                      >
                        Save
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <p style={{ whiteSpace: 'pre-wrap', margin: '4px 0' }}>{n.body}</p>
                    {isAuthor ? (
                      <div style={{ display: 'flex', gap: 4 }}>
                        <Button
                          variant="ghost"
                          onClick={() => startEdit(n)}
                          data-testid={`edit-note-${n.id}`}
                        >
                          <Pencil size={12} /> Edit
                        </Button>
                        <Button
                          variant="ghost"
                          onClick={() => removeNote(n)}
                          data-testid={`delete-note-${n.id}`}
                        >
                          <Trash2 size={12} /> Delete
                        </Button>
                      </div>
                    ) : null}
                  </>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}

/* ------------------------------------------------------------------ */
/*  History panel (intakeform-preview-5xe)                            */
/* ------------------------------------------------------------------ */

function describeAuditEntry(e: AuditTimelineEntry): { title: string; subtitle?: string } {
  const meta = e.metadata;
  switch (e.action) {
    case 'response_drafted':
      return { title: 'Draft saved' };
    case 'response_submitted':
      return { title: 'Submitted by patient' };
    case 'read':
      return { title: 'Viewed' };
    case 'write':
      if (meta['op'] === 'status_change' && typeof meta['fromStatus'] === 'string') {
        return {
          title: 'Status changed',
          subtitle: `${getStatusLabel(meta['fromStatus'] as string)} → ${getStatusLabel(meta['toStatus'] as string)}`,
        };
      }
      return { title: 'Updated' };
    case 'note_created':
      return { title: 'Note added' };
    case 'note_edited':
      return { title: 'Note edited' };
    case 'note_deleted':
      return { title: 'Note deleted' };
    case 'eligibility_checked':
      return { title: 'Eligibility checked' };
    case 'eligibility_recheck_attempted':
      return { title: 'Eligibility re-check attempted' };
    case 'link_opened':
      return { title: 'Magic link opened' };
    case 'attachment_presigned':
      return { title: 'Attachment uploaded' };
    default:
      return { title: e.action.replace(/_/g, ' ') };
  }
}

function HistoryPanel({ responseId }: { responseId: string }) {
  const audit = useResponseAudit(responseId);
  const [open, setOpen] = useState(false);

  const items = audit.data ?? [];

  return (
    <Card
      heading={
        <button
          type="button"
          onClick={() => setOpen((p) => !p)}
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            font: 'inherit',
          }}
        >
          <History size={16} /> History ({items.length}) {open ? '▾' : '▸'}
        </button>
      }
      variant="default"
      data-testid="history-panel"
    >
      {!open ? null : audit.isLoading ? (
        <p className="muted">Loading history…</p>
      ) : items.length === 0 ? (
        <p className="muted">No audit events recorded for this response yet.</p>
      ) : (
        <ol
          style={{
            listStyle: 'none',
            padding: 0,
            margin: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
          }}
        >
          {items.map((e) => {
            const { title, subtitle } = describeAuditEntry(e);
            const actorLabel =
              e.actor.displayName !== null
                ? `${e.actor.displayName}${e.actor.role !== null ? ` (${e.actor.role})` : ''}`
                : e.actor.kind === 'system'
                  ? 'System'
                  : e.actor.kind === 'magic_link'
                    ? 'Patient (via magic link)'
                    : 'Unknown';
            return (
              <li
                key={e.id}
                data-testid={`history-entry-${e.id}`}
                style={{
                  display: 'flex',
                  gap: 12,
                  borderLeft: '2px solid var(--color-border, #e5e7eb)',
                  paddingLeft: 12,
                }}
              >
                <div style={{ minWidth: 130 }} className="muted">
                  {formatDateTime(e.occurredAt)}
                </div>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 'var(--text-sm)' }}>{title}</div>
                  {subtitle ? <div style={{ fontSize: 'var(--text-xs)' }}>{subtitle}</div> : null}
                  <div className="muted" style={{ fontSize: 'var(--text-xs)' }}>
                    {actorLabel}
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </Card>
  );
}

export function ResponseDetailRoute() {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading, error } = useResponse(id);
  const updateStatus = useUpdateResponseStatus();
  const me = useMe();

  // Questionnaire definition is now returned inline from the API
  const qDef = data?.questionnaire as Questionnaire | undefined;

  if (isLoading) {
    return (
      <div className="response-detail-loading">
        <div className="response-detail-spinner" />
        <p className="muted">Loading response…</p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="response-detail-error">
        <div className="error-banner" style={{ maxWidth: 480, margin: '0 auto' }}>
          Failed to load response. The record may have been deleted or you may not have access.
        </div>
        <Link to="/dashboard" style={{ marginTop: 16, display: 'inline-block' }}>
          ← Back to dashboard
        </Link>
      </div>
    );
  }

  const items = data.resource.item ?? [];
  const itemMap = buildItemMap(qDef);
  const sections = buildSections(qDef);

  // Build findings lookup by linkId
  const findingsByLinkId = new Map<string | undefined, Finding[]>();
  for (const f of data.findings) {
    const list = findingsByLinkId.get(f.linkId) ?? [];
    list.push(f);
    findingsByLinkId.set(f.linkId, list);
  }
  const formLevelFindings = findingsByLinkId.get(undefined) ?? [];

  // Build response answers lookup by linkId
  const answersByLinkId = new Map<string, unknown[]>();
  const walkResponseItems = (responseItems: typeof items) => {
    for (const ri of responseItems) {
      answersByLinkId.set(ri.linkId, ri.answer ?? []);
      if (ri.item) walkResponseItems(ri.item);
    }
  };
  walkResponseItems(items);

  // Determine which linkIds are accounted for by sections
  const sectionedLinkIds = new Set<string>();
  for (const sec of sections) {
    for (const lid of sec.children) sectionedLinkIds.add(lid);
  }

  // Unsectioned items (items not belonging to any group)
  const unsectionedLinkIds = [...answersByLinkId.keys()].filter(
    (lid) => !sectionedLinkIds.has(lid),
  );

  const patient = data.patient;
  const patientName = patient?.displayName ?? patient?.email ?? 'Unknown Patient';

  const handleStatusChange = (newStatus: string) => {
    if (!id) return;
    updateStatus.mutate({ id, status: newStatus });
  };

  const formTitle = qDef?.title ?? qDef?.name ?? 'Questionnaire';

  return (
    <div className="response-detail-container">
      {/* ── Breadcrumb / Back link ────────────────────────── */}
      <div className="response-detail-breadcrumb">
        <Link to="/dashboard" className="response-detail-back">
          <ArrowLeft size={16} />
          Back to submissions
        </Link>
        <span className="response-detail-breadcrumb-sep">/</span>
        <span className="response-detail-breadcrumb-current">{formTitle}</span>
      </div>

      {/* ── Patient Profile Header Card ───────────────────── */}
      <Card variant="elevated">
        <div className="response-header">
          <div className="response-header-info">
            <h1 className="response-patient-name">
              {patientName}
              {data.flagged && (
                <>
                  <span className="response-flagged-badge">
                    <AlertTriangle size={12} />
                    Flagged
                  </span>
                  {data.flaggedCodes.length > 0 ? (
                    <span
                      className="muted"
                      data-testid="flagged-because"
                      style={{ fontSize: 'var(--text-xs)', marginLeft: 8, fontWeight: 400 }}
                      title="The finding codes that tripped your org's clinically-significant partition. Edit the partition in Org Settings."
                    >
                      because:{' '}
                      {flagReasons(data.flaggedCodes, data.findings).map((reason, i, all) => (
                        <code key={reason} style={{ marginRight: i === all.length - 1 ? 0 : 4 }}>
                          {reason}
                        </code>
                      ))}
                    </span>
                  ) : null}
                </>
              )}
            </h1>
            <p className="response-id-label">Response {id?.slice(0, 8)}…</p>
          </div>
          <div className="response-header-actions">
            <StatusBadge status={data.status} />
            <select
              value={data.status}
              onChange={(e) => handleStatusChange(e.target.value)}
              disabled={updateStatus.isPending}
              className="response-status-select"
            >
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="response-meta-grid">
          {patient?.email && <MetaItem icon={Mail} label="Email" value={patient.email} />}
          {patient?.phone && <MetaItem icon={Phone} label="Phone" value={patient.phone} />}
          {data.resource.authored && (
            <MetaItem
              icon={Calendar}
              label="Submitted"
              value={formatDateTime(data.resource.authored)}
            />
          )}
          <MetaItem icon={FileText} label="Form" value={formTitle} />
        </div>
      </Card>

      {/* ── Insurance eligibility (bead 27w) ──────────────── */}
      {data.eligibility !== undefined && data.eligibility !== null && (
        <EligibilityCard eligibility={data.eligibility} />
      )}

      {/* ── Form-level findings alert ─────────────────────── */}
      {formLevelFindings.length > 0 && (
        <Card tone="warning" style={{ padding: '16px 20px' }}>
          <div className="response-form-finding-header">
            <AlertTriangle size={16} style={{ color: 'rgb(var(--color-warning))' }} />
            <strong style={{ fontSize: 'var(--text-sm)' }}>
              Form-level findings ({formLevelFindings.length})
            </strong>
          </div>
          {formLevelFindings.map((f, i) => (
            <div key={`form-${i}`} className="response-form-finding-item">
              {f.message}{' '}
              <span className="muted" style={{ fontSize: 'var(--text-xs)' }}>
                ({f.code})
              </span>
            </div>
          ))}
        </Card>
      )}

      {/* ── Sectioned answer cards ────────────────────────── */}
      {sections.map((section) => {
        const sectionAnswerLinkIds = section.children.filter((lid) => answersByLinkId.has(lid));
        if (sectionAnswerLinkIds.length === 0) return null;

        return (
          <Card key={section.linkId} heading={section.text} variant="default">
            {sectionAnswerLinkIds.map((lid) => {
              const questionText = getQuestionLabel(lid, itemMap);
              const answers = answersByLinkId.get(lid) ?? [];
              const findings = findingsByLinkId.get(lid) ?? [];
              return (
                <AnswerRow
                  key={lid}
                  linkId={lid}
                  questionText={questionText}
                  answers={answers}
                  findings={findings}
                />
              );
            })}
          </Card>
        );
      })}

      {/* ── Unsectioned answers (fallback) ────────────────── */}
      {unsectionedLinkIds.length > 0 && (
        <Card
          heading={sections.length > 0 ? 'Additional Details' : 'Patient Responses'}
          variant="default"
        >
          {unsectionedLinkIds.map((lid) => {
            const questionText = getQuestionLabel(lid, itemMap);
            const answers = answersByLinkId.get(lid) ?? [];
            const findings = findingsByLinkId.get(lid) ?? [];
            return (
              <AnswerRow
                key={lid}
                linkId={lid}
                questionText={questionText}
                answers={answers}
                findings={findings}
              />
            );
          })}
        </Card>
      )}

      {/* ── Notes panel + History panel (bib / 5xe) ──────── */}
      {id !== undefined ? (
        <>
          <NotesPanel responseId={id} currentUserId={me.data?.user.id} />
          <HistoryPanel responseId={id} />
        </>
      ) : null}

      {/* ── Findings summary (if flagged) ─────────────────── */}
      {data.findings.length > 0 && (
        <Card heading={`All Findings (${data.findings.length})`} tone="flagged" variant="default">
          {data.findings.map((f, i) => (
            <div key={`finding-${i}`} className="response-finding-row">
              <AlertTriangle
                size={14}
                className={`response-finding-icon ${f.severity === 'error' ? 'severity-error' : 'severity-warning'}`}
              />
              <div>
                <div className="response-finding-message">{f.message}</div>
                <div className="response-finding-meta">
                  {f.linkId ? (
                    <span>
                      Field: <strong>{getQuestionLabel(f.linkId, itemMap)}</strong>
                    </span>
                  ) : (
                    <span>Form-level</span>
                  )}{' '}
                  · {f.severity} · <code>{f.code}</code>
                </div>
              </div>
            </div>
          ))}
        </Card>
      )}
    </div>
  );
}
